import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  Button,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Category } from '../models/Category';
import { Product } from '../models/Product';
import categoryService from '../services/categoryService';
import productService from '../services/productService';

interface CategoryProductSelectorProps {
  selectedProductIds: string[];
  onSelectionChange: (productIds: string[]) => void;
  categories?: Category[];
  products?: Product[];
  loading?: boolean;
}

const normalizeValue = (value?: string | null): string =>
  (value || '').trim().toLowerCase();

const getCategoryLookupValues = (category: Category): string[] =>
  [category.id, category.name].map(normalizeValue).filter(Boolean);

const getProductCategoryLookupValues = (product: Product): string[] =>
  [product.categoryId, product.categoryKey, product.category]
    .map(normalizeValue)
    .filter(Boolean);

const productMatchesCategory = (product: Product, category: Category): boolean => {
  const categoryValues = getCategoryLookupValues(category);
  const productValues = getProductCategoryLookupValues(product);

  return productValues.some((value) => categoryValues.includes(value));
};

const buildActiveCategories = (categories: Category[]): Category[] =>
  categories.filter((category) => !category.isAddonCategory && category.isActive !== false);

const filterMainProducts = (products: Product[], categories: Category[]): Product[] => {
  const addonCategories = categories.filter((category) => category.isAddonCategory);

  if (addonCategories.length === 0) {
    return products;
  }

  return products.filter(
    (product) => !addonCategories.some((category) => productMatchesCategory(product, category))
  );
};

const CategoryProductSelector: React.FC<CategoryProductSelectorProps> = ({
  selectedProductIds,
  onSelectionChange,
  categories: providedCategories,
  products: providedProducts,
  loading: externalLoading = false,
}) => {
  // States
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  const usingProvidedData =
    Array.isArray(providedCategories) && Array.isArray(providedProducts);

  const availableCategories = React.useMemo(
    () =>
      usingProvidedData
        ? buildActiveCategories(providedCategories)
        : categories,
    [categories, providedCategories, usingProvidedData]
  );

  const availableProducts = React.useMemo(
    () =>
      usingProvidedData
        ? filterMainProducts(providedProducts, providedCategories)
        : products,
    [products, providedCategories, providedProducts, usingProvidedData]
  );
  
  // Fetch categories and products
  useEffect(() => {
    if (usingProvidedData) {
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const categoriesData = await categoryService.getCategories();
        const activeNonAddonCategories = buildActiveCategories(categoriesData);
        setCategories(activeNonAddonCategories);

        const productsData = await productService.getProducts();
        setProducts(filterMainProducts(productsData, categoriesData));
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [usingProvidedData]);

  useEffect(() => {
    if (!availableCategories.length) {
      setSelectedCategory(null);
      return;
    }

    setSelectedCategory((prev) => {
      if (prev && availableCategories.some((category) => category.id === prev.id)) {
        return prev;
      }
      return availableCategories[0];
    });
  }, [availableCategories]);
  
  // Update filtered products when category or search changes
  useEffect(() => {
    if (!availableProducts.length) {
      setFilteredProducts([]);
      return;
    }
    
    let filtered = availableProducts;
    
    // Filter by category if selected - using the same approach as Products.tsx
    if (selectedCategory) {
      // Special virtual category: "promotions" -> use isOnPromotion flag instead of product.category
      if (selectedCategory.id === 'promotions') {
        filtered = availableProducts.filter((product) => product.isOnPromotion === true);
      } else {
        filtered = availableProducts.filter((product) =>
          productMatchesCategory(product, selectedCategory)
        );
      }
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(term) || 
        p.description?.toLowerCase().includes(term)
      );
    }
    
    setFilteredProducts(filtered);
  }, [availableProducts, searchTerm, selectedCategory]);
  
  // Handle category selection
  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category);
    setSearchTerm('');
  };
  
  // Handle product toggle selection
  const handleProductToggle = (productId: string) => {
    if (selectedProductIds.includes(productId)) {
      onSelectionChange(selectedProductIds.filter(id => id !== productId));
    } else {
      onSelectionChange([...selectedProductIds, productId]);
    }
  };
  
  // Handle select all in current category
  const handleSelectAllInCategory = () => {
    if (!selectedCategory) return;
    
    const categoryProductIds = filteredProducts.map(p => p.id);
    const newSelectedIds = [...selectedProductIds];
    
    // Add all category products that aren't already selected
    categoryProductIds.forEach(id => {
      if (!newSelectedIds.includes(id)) {
        newSelectedIds.push(id);
      }
    });
    
    onSelectionChange(newSelectedIds);
  };
  
  // Handle deselect all in current category
  const handleDeselectAllInCategory = () => {
    if (!selectedCategory) return;
    
    const categoryProductIds = filteredProducts.map(p => p.id);
    const newSelectedIds = selectedProductIds.filter(id => !categoryProductIds.includes(id));
    
    onSelectionChange(newSelectedIds);
  };

  // Handle select all products (all categories)
  const handleSelectAllProducts = () => {
    const allProductIds = availableProducts.map((product) => product.id);
    const newSelectedIds = [...selectedProductIds];

    allProductIds.forEach(id => {
      if (!newSelectedIds.includes(id)) {
        newSelectedIds.push(id);
      }
    });

    onSelectionChange(newSelectedIds);
  };
  
  // Handle clearing all selections
  const handleClearAll = () => {
    onSelectionChange([]);
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack 
        direction={{ xs: 'column', md: 'row' }} 
        spacing={2} 
        sx={{ 
          flexGrow: 1, 
          minHeight: { xs: 300, md: 400 },
          maxHeight: { xs: '60vh', md: '70vh' }
        }}
      >
        {/* Categories Panel */}
        <Box sx={{ width: { xs: '100%', md: '25%' }, minHeight: { xs: 150, md: 'auto' } }}>
          <Paper sx={{ 
            height: '100%', 
            overflow: 'auto', 
            p: 1,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '10px',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.1))',
              pointerEvents: 'none'
            }
          }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ p: 1 }}>
              Catégories
            </Typography>
            <Divider />
            <List dense>
              {availableCategories.map((category) => (
                <ListItemButton
                  key={category.id}
                  selected={selectedCategory?.id === category.id}
                  onClick={() => handleCategoryClick(category)}
                >
                  <ListItemText primary={category.name} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Box>
        
        {/* Products Panel */}
        <Box sx={{ width: { xs: '100%', md: '45%' } }}>
          <Paper sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            p: 1,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '10px',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.1))',
              pointerEvents: 'none',
              zIndex: 1
            }
          }}>
            <Box sx={{ p: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Produits {selectedCategory ? `- ${selectedCategory.name}` : ''}
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                margin="dense"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllInCategory}
                  disabled={!selectedCategory || filteredProducts.length === 0}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  Tout sélectionner (catégorie)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDeselectAllInCategory}
                  disabled={!selectedCategory || filteredProducts.length === 0}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  Tout désélectionner (catégorie)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={handleSelectAllProducts}
                  disabled={products.length === 0}
                  sx={{ flexBasis: '100%' }}
                >
                  Sélectionner tous les produits
                </Button>
              </Box>
            </Box>
            <Divider />
            <List sx={{ overflow: 'auto', flexGrow: 1 }}>
              {loading || externalLoading ? (
                <ListItem>
                  <ListItemText primary="Chargement des produits…" />
                </ListItem>
              ) : filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <ListItem
                    key={product.id}
                    dense
                    disablePadding
                    secondaryAction={
                      <Checkbox
                        edge="end"
                        onChange={() => handleProductToggle(product.id)}
                        checked={selectedProductIds.includes(product.id)}
                      />
                    }
                  >
                    <ListItemButton onClick={() => handleProductToggle(product.id)}>
                      <ListItemText 
                        primary={product.name} 
                        secondary={`${product.price} ${product.description ? '- ' + product.description : ''}`} 
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="Aucun produit trouvé" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Box>
        
        {/* Selected Products Panel */}
        <Box sx={{ width: { xs: '100%', md: '30%' } }}>
          <Paper sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            p: 1,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '10px',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.1))',
              pointerEvents: 'none',
              zIndex: 1
            }
          }}>
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Produits sélectionnés ({selectedProductIds.length})
              </Typography>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={handleClearAll}
                disabled={selectedProductIds.length === 0}
              >
                Vider la liste
              </Button>
            </Box>
            <Divider />
            <List sx={{ overflow: 'auto', flexGrow: 1 }}>
              {selectedProductIds.length > 0 ? (
                availableProducts
                  .filter(p => selectedProductIds.includes(p.id))
                  .map((product) => {
                    const categoryName =
                      availableCategories.find((category) => productMatchesCategory(product, category))
                        ?.name ||
                      product.category ||
                      'Catégorie inconnue';
                    
                    return (
                      <ListItem
                        key={product.id}
                        dense
                        disablePadding
                        secondaryAction={
                          <IconButton size="small" onClick={() => handleProductToggle(product.id)}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemButton onClick={() => handleProductToggle(product.id)}>
                          <ListItemText 
                            primary={product.name} 
                            secondary={categoryName} 
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })
              ) : (
                <ListItem>
                  <ListItemText primary="Aucun produit sélectionné" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};

export default CategoryProductSelector;
